import type { KeyspaceDirectoryEntry } from "@legal-500/redis-db-manager-types";
import type { SlotStatus } from "./StatusBadge.js";

export type SlotComputedStatus = {
  status: SlotStatus;
  missingPageTypes: string[];
};

export function computeSlotStatus(
  entry: KeyspaceDirectoryEntry,
  isActive: boolean,
  allPageTypes: string[],
): SlotComputedStatus {
  if (isActive) {
    return { status: "active", missingPageTypes: [] };
  }

  if (entry.status === "running") {
    return { status: "running", missingPageTypes: [] };
  }

  if (entry.status === "failed") {
    return { status: "failed", missingPageTypes: [] };
  }

  if (entry.status === "empty") {
    return { status: "empty", missingPageTypes: [] };
  }

  const syncedNames = new Set(entry.pageTypes.map(pt => pt.name));
  const missing = allPageTypes.filter(name => !syncedNames.has(name));

  if (allPageTypes.length === 0 || missing.length === 0) {
    return { status: "available", missingPageTypes: [] };
  }

  return { status: "partial", missingPageTypes: missing };
}

export function splitLabel(label: string): { prefix: string; suffix: string } {
  const match = label.match(/^(.+)-([a-z0-9]{6})$/);
  if (match && match[1] && match[2]) {
    return { prefix: match[1], suffix: `-${match[2]}` };
  }
  return { prefix: label, suffix: "" };
}

export function formatTimestamp(ms: number | null): string {
  if (!ms || ms <= 0) return "-";
  return new Date(ms).toLocaleString();
}

export function formatDurationMs(ms: number | null | undefined): string {
  if (!ms || ms <= 0) return "-";
  const seconds = Math.floor(ms / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  if (hours > 0) {
    return `${hours}h ${minutes % 60}m`;
  }
  if (minutes > 0) {
    return `${minutes}m ${seconds % 60}s`;
  }
  return `${seconds}s`;
}

export function humanizeBytes(bytes: number | null | undefined): string {
  if (!bytes || bytes <= 0) return "0 B";
  const units = ["B", "KB", "MB", "GB", "TB"];
  let value = bytes;
  let unitIndex = 0;
  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex += 1;
  }
  return `${value.toFixed(value >= 100 ? 0 : 1)} ${units[unitIndex]}`;
}
